export type APDocumentType =
  | "Invoice"
  | "FBT Report"
  | "Payroll Advice"
  | "Reconciliation Invoice"
  | "Termination Advice"
  | "Engagement Report"
  | "Promotion"
  | "Packaging Agreement"
  | "Luxury Lease Report"
  | "Credit Note"
  | "Pre-Reconciliation Report"
  | "Post-Reconciliation Report"
  | "Insurance Certificate of Currency";

export type APDocumentMediaType = "file" | "url";

export type APDocumentContentType = "file" | "website" | "video";

export type APDocument = {
  id: string;
  externalId: string;
  name: string;
  date: string | null;
  date2: string | null;
  accountId: string;
  accountName: string;
  documentType: APDocumentType;
  amount: number | null;
  note: string | null;
  isVisible: boolean;
  fileName: string | null;
  url: string | null;
  mediaType: APDocumentMediaType;
  contentType: APDocumentContentType;
};

export type DocumentQueryParams = {
  documentTypes: APDocumentType[];
  page?: string;
  pageSize?: string;
  accountIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  date2From?: string;
  date2To?: string;
  nameSearch?: string;
};

export type DocumentFilters = {
  pageSize: number;
  nextPageLink?: string;
  accountIds?: string[];
  documentTypes?: APDocumentType[];
  dateFrom?: string;
  dateTo?: string;
  date2From?: string;
  date2To?: string;
  nameSearch?: string;
};

export type InvoiceComparisonSortOption = "date" | "status";

const invoiceComparisonStatusSortOrder = ["missing", "matching"];

export type InvoiceInComparisonSummary = {
  id: string;
  invoiceDate: string;
  dueDate: string;
  amount: number;
  gst: number;
};

export type InvoiceComparisonSummary = {
  invoiceId: string;
  status: "matching" | "missing";
  ceInvoice: InvoiceInComparisonSummary | null;
  crmInvoice: InvoiceInComparisonSummary | null;
};

export const getDateForInvoiceComparison = (
  invoiceComparison: InvoiceComparisonSummary
) => {
  const date =
    invoiceComparison.ceInvoice?.invoiceDate ||
    invoiceComparison.crmInvoice?.invoiceDate;
  return date ?? "";
};

export const filterInvoiceComparisons = (
  comparisons: InvoiceComparisonSummary[],
  comparisonStatus: "matching" | "missing" | null
): InvoiceComparisonSummary[] => {
  return comparisons.filter((comparison) => {
    const comparisonStatusMatches = comparisonStatus
      ? comparison.status === comparisonStatus
      : true;
    return comparisonStatusMatches;
  });
};

export const sortInvoiceComparisons = (
  comparisons: InvoiceComparisonSummary[],
  invoiceComparisonSortOptionId: InvoiceComparisonSortOption
): InvoiceComparisonSummary[] => {
  const comparisonsToSort = [...comparisons];

  let compareFunction: (
    a: InvoiceComparisonSummary,
    b: InvoiceComparisonSummary
  ) => number;

  switch (invoiceComparisonSortOptionId) {
    case "date":
      compareFunction = (
        a: InvoiceComparisonSummary,
        b: InvoiceComparisonSummary
      ) => {
        const aDate = getDateForInvoiceComparison(a);
        const bDate = getDateForInvoiceComparison(b);
        if (aDate > bDate) {
          return -1;
        }
        if (aDate < bDate) {
          return 1;
        }
        return 0;
      };
      break;
    case "status":
      compareFunction = (
        a: InvoiceComparisonSummary,
        b: InvoiceComparisonSummary
      ) => {
        const aStatus = invoiceComparisonStatusSortOrder.indexOf(a.status);
        const bStatus = invoiceComparisonStatusSortOrder.indexOf(b.status);
        if (aStatus > bStatus) {
          return 1;
        }
        if (aStatus < bStatus) {
          return -1;
        }
        return 0;
      };
      break;
  }

  comparisonsToSort.sort(compareFunction);

  return comparisonsToSort;
};
